/**
 * VisualRenderer Component (Phase 2 + Phase 3: Safe Rendering + Advanced Rendering)
 *
 * Safely renders markdown content with visual styling including diagrams
 * Active only when RESULTS_NEW_DESIGN flag is enabled
 * Uses react-markdown for XSS-safe rendering + Mermaid for diagrams
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MermaidDiagram } from './MermaidDiagram';
import { CodeBlock } from './CodeBlock';
import { TableWrapper } from './TableWrapper';

interface VisualRendererProps {
  content: string;
  className?: string;
}

/**
 * Renders markdown content with safe HTML sanitization
 * Supports: headings, bold, italic, links, lists, tables, code blocks
 */
export const VisualRenderer: React.FC<VisualRendererProps> = ({
  content,
  className = '',
}) => {
  return (
    <div className={`prose prose-invert prose-sm max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom styling for code blocks (Phase 3: with diagram support + copy button)
          code: ({ inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            const codeString = String(children).trim();

            // Check if this is a diagram (mermaid, dot, graphviz, plantuml)
            const isDiagram = /^(mermaid|dot|graphviz|plantuml)$/i.test(language);

            // Render inline code
            if (inline) {
              return <CodeBlock code={codeString} inline={true} />;
            }

            // Render diagram with MermaidDiagram component
            if (isDiagram) {
              return <MermaidDiagram code={codeString} language={language} />;
            }

            // Regular code block with copy button
            return <CodeBlock code={codeString} language={language} />;
          },

          // Custom styling for links
          a: ({ children, href, ...props }) => (
            <a
              href={href}
              className="text-cyan-400 hover:text-cyan-300 underline"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          ),

          // Custom styling for tables with copy button
          table: ({ children, ...props }) => (
            <TableWrapper>
              <table
                className="min-w-full border border-slate-700/50 rounded-lg"
                {...props}
              >
                {children}
              </table>
            </TableWrapper>
          ),

          thead: ({ children, ...props }) => (
            <thead className="bg-slate-800/50" {...props}>
              {children}
            </thead>
          ),

          th: ({ children, ...props }) => (
            <th
              className="px-4 py-2 text-left text-sm font-semibold text-slate-300 border-b border-slate-700/50"
              {...props}
            >
              {children}
            </th>
          ),

          td: ({ children, ...props }) => (
            <td
              className="px-4 py-2 text-sm text-slate-400 border-b border-slate-700/50"
              {...props}
            >
              {children}
            </td>
          ),

          // Custom styling for lists
          ul: ({ children, ...props }) => (
            <ul className="list-disc list-inside space-y-1 my-2" {...props}>
              {children}
            </ul>
          ),

          ol: ({ children, ...props }) => (
            <ol className="list-decimal list-inside space-y-1 my-2" {...props}>
              {children}
            </ol>
          ),

          li: ({ children, ...props }) => (
            <li className="text-slate-50" {...props}>
              {children}
            </li>
          ),

          // Custom styling for headings
          h1: ({ children, ...props }) => (
            <h1 className="text-2xl font-bold text-white mt-4 mb-2" {...props}>
              {children}
            </h1>
          ),

          h2: ({ children, ...props }) => (
            <h2 className="text-xl font-bold text-white mt-3 mb-2" {...props}>
              {children}
            </h2>
          ),

          h3: ({ children, ...props }) => (
            <h3 className="text-lg font-semibold text-white mt-2 mb-1" {...props}>
              {children}
            </h3>
          ),

          // Custom styling for images
          img: ({ src, alt, ...props }) => (
            <img
              src={src}
              alt={alt || 'Image'}
              className="max-w-full h-auto rounded-lg my-2"
              loading="lazy"
              {...props}
            />
          ),

          // Custom styling for blockquotes
          blockquote: ({ children, ...props }) => (
            <blockquote
              className="border-l-4 border-cyan-500/50 pl-4 italic text-slate-400 my-2"
              {...props}
            >
              {children}
            </blockquote>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
